import { AHText, Box } from "arkh";
import React, { useEffect, useMemo, useState } from "react";

import { Platform, StyleSheet } from "react-native";
import { FileIcon, FileIconType } from "./FileListItem/FileIcon";
import { getFilesAsync } from "./fileUtils/getFilesAsync";
import { PendingFileFile } from "./FileUploadArea";
import cuid from "cuid";
import { removeExtension } from "./removeExtension";
import { SUPPORTED_FILE_TYPES } from "./fileUtils/fileConstants";

type DropFileZoneState = "idle" | "drag_allowed" | "drag_error";

type PreviewTypes =
  | "IMG"
  | "PDF"
  | "DOC"
  | "XLS"
  | "PPT"
  | "DIRECTORY"
  | "OTHER";

type DropedFile = {
  file: File;
  type: string;
};

async function getPreviewFiles(dataTransfer: DataTransfer, types?: string[]) {
  const fileTypes = new Set<PreviewTypes>();
  for (let i = 0; i < dataTransfer.items.length; i++) {
    const item = dataTransfer.items[i];

    if (item.kind === "file" && item.type === "") {
      fileTypes.add("DIRECTORY");
    }

    const type = item.type.toLowerCase();

    if (
      (!types || types?.some((t) => type.includes(t))) &&
      item.kind === "file"
    ) {
      if (type.includes("pdf")) {
        fileTypes.add("PDF");
      } else if (type.includes("msword") || type.includes("wordprocessingml")) {
        fileTypes.add("DOC");
      } else if (
        type.includes("application/vnd.ms-excel") ||
        type.includes(
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        )
      ) {
        fileTypes.add("XLS");
      } else if (type.includes("image/")) {
        fileTypes.add("IMG");
      }
      if (
        type.includes("application/vnd.ms-powerpoint") ||
        type.includes(
          "application/vnd.openxmlformats-officedocument.presentationml.presentation"
        )
      ) {
        fileTypes.add("PPT");
      } else {
        fileTypes.add("OTHER");
      }
    }
  }

  return fileTypes;
}

function IconType(props: { fileType: FileIconType; index: number }) {
  const [isVisable, setIsVisable] = useState(false);

  return (
    <Box
      color="surface"
      shadow="strong"
      onLayout={() => {
        setIsVisable(true);
      }}
      style={{
        width: 120,
        height: 120,
        justifyContent: "center",
        alignItems: "center",
        position: "absolute",
        ...{ transition: "transform 0.2s ease-in-out" },
        transform: [
          ...(isVisable
            ? [
                {
                  translateX: -140,
                },
                {
                  rotate: `${(props.index - 1) * 20}deg`,
                },
                {
                  translateX: 140,
                },
              ]
            : []),
          {
            translateY: -140,
          },
        ],
      }}
      cornerRadius="large"
    >
      <FileIcon filetype={props.fileType} size={80} />
    </Box>
  );
}

export async function droppedFileToImage(file: File) {
  const url = URL.createObjectURL(file);
  const dimensions = await new Promise<{ width: number; height: number }>(
    (resolve, reject) => {
      const img = new Image();

      img.onload = () => {
        const { naturalWidth: width, naturalHeight: height } = img;
        resolve({ width, height });
      };

      // and this handler will fire if there was an error with the image (like if it's not really an image or a corrupted one)
      img.onerror = () => {
        reject("There was some problem with the image.");
      };

      img.src = url;
    }
  );
  return {
    width: dimensions.width,
    height: dimensions.height,
    uri: url,
  };
}
export function DropFileZone(props: {
  children: React.ReactNode;
  deactivated?: boolean;
  type: "FILE" | "IMAGE" | "FILE_AND_IMAGE";
  maxSize?: number;
  disableForeground?: boolean;

  onDropFiles?: (
    files: DropedFile[],
    nonApprovedFiles: { reason: "size" | "type"; file: DropedFile }[]
  ) => void;

  onDropFilesNew?: (
    files: (PendingFileFile & {
      abstractType: "FILE" | "IMAGE";
    })[]
  ) => void;
}) {
  const types = useMemo(() => {
    switch (props.type) {
      case "FILE":
        return SUPPORTED_FILE_TYPES;
      case "IMAGE":
        return ["image/"];
      case "FILE_AND_IMAGE":
        return [...SUPPORTED_FILE_TYPES, "image/"];
    }
  }, [props.type]);
  const [state, setState] = useState<DropFileZoneState>("idle");
  const [previewFiles, setPreviewFiles] = useState<Set<PreviewTypes>>(
    new Set()
  );

  const isRenderningPreview = state === "drag_allowed";

  useEffect(() => {
    if (isRenderningPreview) {
      const timeout = setTimeout(() => {
        setState("idle");
      }, 3000);
      return () => {
        clearTimeout(timeout);
      };
    }
  }, [isRenderningPreview]);

  useEffect(() => {
    if (Platform.OS !== "web") {
      return;
    }
    if (props.deactivated) {
      return;
    }
    const fun = (event: ClipboardEvent) => {
      if (event.clipboardData && event.clipboardData.files.length > 0) {
        event.preventDefault();
        const approved: DropedFile[] = [];
        const nonApproved: { reason: "size" | "type"; file: DropedFile }[] = [];
        for (const droppedFile of event.clipboardData.files) {
          const f = { file: droppedFile, type: droppedFile.type };
          if (!types.some((t) => droppedFile.type.toLowerCase().includes(t))) {
            nonApproved.push({ reason: "type", file: f });
          } else if (props.maxSize && droppedFile.size > props.maxSize) {
            nonApproved.push({ reason: "size", file: f });
          } else {
            approved.push(f);
          }

          props.onDropFilesNew?.(
            approved?.map(({ file, type }) => ({
              file,
              abstractType: type.includes("image/") ? "IMAGE" : "FILE",
              type,
              name: removeExtension(file.name),
              id: cuid(),
              size: file.size,
            }))
          );

          props.onDropFiles?.(approved, []);
          setState("idle");
        }
      }
    };
    document.addEventListener("paste", fun);

    return () => {
      document.removeEventListener("paste", fun);
    };
  }, [props, types]);

  const renderPreviewContent = () => {
    return (
      <>
        {props.disableForeground ? null : (
          <div
            style={{
              ...StyleSheet.absoluteFillObject,
              backgroundColor: "rgba(0,0,0,0.5)",
              alignContent: "center",
              alignItems: "center",
              justifyContent: "center",
              justifyItems: "center",
            }}
          >
            <Box
              style={{
                flexDirection: "row",
                justifyContent: "center",
                alignItems: "center",
                flex: 1,
                height: "100%",
              }}
            >
              {Array.from(previewFiles).map((fileType, index) => {
                if (fileType === "DIRECTORY" || fileType === "OTHER") {
                  return null;
                }
                return (
                  <IconType
                    key={fileType}
                    fileType={fileType}
                    index={index + 1}
                  />
                );
              })}

              <AHText
                style={{
                  transform: [
                    {
                      translateY: 100,
                    },
                  ],
                }}
                type="title-big"
                color="lightText"
              >
                {props.type === "IMAGE"
                  ? "Ladda upp bilder"
                  : "Ladda upp filer"}
              </AHText>
            </Box>
          </div>
        )}
        <div
          onDragLeave={() => {
            setState("idle");
          }}
          onDrop={async (event) => {
            console.log("drop");
            event.preventDefault();
            event.persist();
            const dropedFiles = await getFilesAsync(event.dataTransfer);

            const approved: DropedFile[] = [];
            const nonApproved: { reason: "size" | "type"; file: DropedFile }[] =
              [];

            for (const droppedFile of dropedFiles) {
              if (
                !types.some((t) => droppedFile.type.toLowerCase().includes(t))
              ) {
                nonApproved.push({ reason: "type", file: droppedFile });
              } else if (
                props.maxSize &&
                droppedFile.file.size > props.maxSize
              ) {
                nonApproved.push({ reason: "size", file: droppedFile });
              } else {
                approved.push(droppedFile);
              }
            }
            props.onDropFiles?.(approved, nonApproved);
            props.onDropFilesNew?.(
              approved?.map(({ file, type }) => ({
                file,
                type,
                abstractType: type.includes("image/") ? "IMAGE" : "FILE",
                name: removeExtension(file.name),
                id: cuid(),
                size: file.size,
              }))
            );
            setState("idle");
          }}
          onDragOver={(event) => {
            console.log("drag over");
            event.preventDefault();
            return false;
          }}
          style={{
            ...StyleSheet.absoluteFillObject,

            alignContent: "center",
            alignItems: "center",
            justifyContent: "center",
            justifyItems: "center",
          }}
        />
      </>
    );
  };
  if (Platform.OS !== "web" || props.deactivated) {
    return <>{props.children}</>;
  }

  return (
    <div
      style={{
        alignItems: "stretch",
        backgroundColor: "transparent",
        border: "0 solid black",
        boxSizing: "border-box",
        display: "flex",
        flexBasis: "auto",
        flexDirection: "column",
        flexShrink: 0,
        listStyle: "none",
        margin: 0,
        minHeight: 0,
        minWidth: 0,
        padding: 0,
        position: "relative",
        textDecoration: "none",
        zIndex: 0,
        flex: 1,
      }}
      onDragEnter={async (event: any) => {
        event.persist();
        event.preventDefault();
        const previewFiles = await getPreviewFiles(event.dataTransfer, types);
        setPreviewFiles(previewFiles);
        setState("drag_allowed");
      }}
      onDragLeave={() => {}}
      onDragOver={(event) => {
        event.persist();
        event.preventDefault();
      }}
    >
      {props.children}
      {state === "drag_allowed" ? renderPreviewContent() : null}
    </div>
  );
}
